import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CourageEditor = () => {
  const [form, setForm] = useState({
    titleEn: "",
    titleAr: "",
    titleKu: "",
    priceEn: "",
    priceAr: "",
    priceKu: "",
    button1En: "",
    button1Ar: "",
    button1Ku: "",
    button2En: "",
    button2Ar: "",
    button2Ku: "",
  });

  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  // modal
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  // -----------------------------
  // helper
  // -----------------------------
  const resolveUrl = (u) => (u?.startsWith("http") ? u : `${API_BASE}${u}`);

  // -----------------------------
  // FETCH DATA
  // -----------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/CourageHeroSection?lang=en`),
          axios.get(`${API_BASE}/api/CourageHeroSection?lang=ar`),
          axios.get(`${API_BASE}/api/CourageHeroSection?lang=ku`),
        ]);

        setForm({
          titleEn: en.data.title,
          priceEn: en.data.price,
          button1En: en.data.button1,
          button2En: en.data.button2,

          titleAr: ar.data.title,
          priceAr: ar.data.price,
          button1Ar: ar.data.button1,
          button2Ar: ar.data.button2,

          titleKu: ku.data.title,
          priceKu: ku.data.price,
          button1Ku: ku.data.button1,
          button2Ku: ku.data.button2,
        });

        setImages(en.data.images || []);
        setPreview(
          en.data.images?.[0]
            ? resolveUrl(en.data.images[0])
            : null
        );

        setLoading(false);
      } catch (err) {
        console.error("Error loading courage hero:", err);
      }
    };

    fetchData();
  }, []);

  // -----------------------------
  // Handle text change
  // -----------------------------
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // -----------------------------
  // SAVE TEXT
  // -----------------------------
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/CourageHeroSection/1`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Courage hero updated!");
    } catch (err) {
      console.error(err);
      alert("Failed to update!");
    }
  };

  // -----------------------------
  // DELETE IMAGE
  // -----------------------------
  const handleDeleteImage = async (url) => {
    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/CourageHeroSection/1/image`, {
        params: { url },
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Image removed.");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // -----------------------------
  // REPLACE IMAGE
  // -----------------------------
  const handleReplace = async (oldUrl, file) => {
    if (!file) return;

    const token = localStorage.getItem("token");

    try {
      // delete old
      await axios.delete(`${API_BASE}/api/CourageHeroSection/1/image`, {
        params: { url: oldUrl },
        headers: { Authorization: `Bearer ${token}` },
      });

      const fd = new FormData();
      fd.append("ImageFiles", file);
      fd.append("ReplaceImages", false);

      await axios.post(`${API_BASE}/api/CourageHeroSection/1/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image updated!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Update failed.");
    }
  };

  // -----------------------------
  // UPLOAD NEW IMAGE
  // -----------------------------
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file.");

    const token = localStorage.getItem("token");
    const fd = new FormData();

    fd.append("ImageFiles", uploadFile);
    fd.append("ReplaceImages", false);

    try {
      await axios.post(`${API_BASE}/api/CourageHeroSection/1/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image uploaded!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed!");
    }
  };

  // -----------------------------
  // UI
  // -----------------------------
  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Courage Hero Editor</h2>

      <div className="row">
        {["En", "Ar", "Ku"].map((lang) => (
          <div className="col-lg-4 col-md-6 mb-4" key={lang}>
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="text-primary mb-3">{lang}</h5>

                <label className="form-label">Title</label>
                <input
                  name={`title${lang}`}
                  className="form-control mb-3"
                  value={form[`title${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Price</label>
                <textarea
                  rows={2}
                  name={`price${lang}`}
                  className="form-control mb-3"
                  value={form[`price${lang}`]}
                  onChange={handleChange}
                />

                <div className="row">
                  <div className="col">
                    <label className="form-label">Button 1</label>
                    <input
                      name={`button1${lang}`}
                      className="form-control"
                      value={form[`button1${lang}`]}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col">
                    <label className="form-label">Button 2</label>
                    <input
                      name={`button2${lang}`}
                      className="form-control"
                      value={form[`button2${lang}`]}
                      onChange={handleChange}
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save All
        </button>
      </div>

      {/* PREVIEW */}
      <h4>Current Background Image</h4>
      {preview && (
        <img
          src={preview}
          className="img-fluid rounded mb-4"
          style={{ maxHeight: "350px", objectFit: "cover" }}
        />
      )}

      {/* Media Manager */}
      <h3 className="mt-4">Images</h3>

      <button
        className="btn btn-success my-3"
        onClick={() => setShowModal(true)}
      >
        + Add New Image
      </button>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Preview</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {images.map((img, idx) => (
            <tr key={idx}>
              <td>
                <img
                  src={resolveUrl(img)}
                  className="img-fluid rounded"
                  style={{ maxHeight: "120px" }}
                />
              </td>

              <td className="d-flex flex-column gap-2">
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteImage(img)}
                >
                  Delete
                </button>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => document.getElementById(`replace_${idx}`).click()}
                >
                  Replace
                </button>

                <input
                  type="file"
                  id={`replace_${idx}`}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={(e) =>
                    handleReplace(img, e.target.files ? e.target.files[0] : null)
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* UPLOAD MODAL */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5>Upload Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default CourageEditor;
